import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import CloseIcon from '@material-ui/icons/Close'
import { createUniversalMerchant } from '../actions/merchantActions'
import { MERCHANT_CREATE_RESET } from '../constants/merchantConstants'

const sections = [
  {
    title: 'On Board',
    eyebrow: 'Getting started',
    description: 'Everything you need for a smooth and simple onboarding experience.',
    actionLabel: 'Open onboarding form',
    Icon: AssignmentIndIcon,
    accent: 'blue',
  },
]

const accentStyles = {
  blue: {
    card: 'hover:border-blue-200 hover:shadow-blue-100/70',
    icon: 'bg-blue-50 text-blue-600 ring-blue-100',
    line: 'from-blue-500 to-cyan-400',
    action: 'text-blue-600 group-hover:text-blue-700',
  },
}

const emptyForm = {
  name: '',
  contactNumber: '',
  email: '',
  location: '',
  pincode: '',
  upiId: '',
}

const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'

const validateForm = values => {
  const errors = {}

  if (!values.name.trim()) errors.name = 'Name is required.'
  else if (!/^[A-Za-z][A-Za-z\s.'-]{1,48}$/.test(values.name.trim())) errors.name = 'Name should contain only letters.'

  if (!/^\d{10}$/.test(values.contactNumber)) errors.contactNumber = 'Contact number must be 10 digits.'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = 'Enter a valid email address.'
  if (values.pincode && !/^\d{6}$/.test(values.pincode)) errors.pincode = 'Pincode must be 6 digits.'
  if (values.upiId && !/^[\w.-]{2,256}@[a-zA-Z]{2,64}$/.test(values.upiId.trim())) errors.upiId = 'Enter a valid UPI ID.'

  return errors
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default function UniversalPage() {
  const dispatch = useDispatch()
  const { loading } = useSelector(state => state.merchantCreate || {})
  const [isOnboardOpen, setIsOnboardOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [touched, setTouched] = useState({})

  const errors = validateForm(form)
  const setValue = (field, value) => setForm(current => ({ ...current, [field]: value }))
  const showError = field => touched[field] ? errors[field] : ''

  const closeOnboard = useCallback(() => {
    if (loading) return
    setIsOnboardOpen(false)
    setForm(emptyForm)
    setTouched({})
    dispatch({ type: MERCHANT_CREATE_RESET })
  }, [dispatch, loading])

  const submitOnboard = async event => {
    event.preventDefault()
    const currentErrors = validateForm(form)

    if (Object.keys(currentErrors).length) {
      setTouched({ name: true, contactNumber: true, email: true, pincode: true, upiId: true })
      return
    }

    try {
      await dispatch(createUniversalMerchant({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        location: form.location.trim(),
        pincode: form.pincode.trim(),
        upiId: form.upiId.trim(),
      }))
      toast.success('Merchant submitted for admin onboarding')
      setIsOnboardOpen(false)
      setForm(emptyForm)
      setTouched({})
      dispatch({ type: MERCHANT_CREATE_RESET })
    } catch (error) {
      toast.error(error.message || 'Unable to onboard merchant')
    }
  }

  useEffect(() => {
    if (!isOnboardOpen) return undefined
    const onKeyDown = event => {
      if (event.key !== 'Escape') return
      if (isOnboardOpen && !loading) closeOnboard()
    }
    document.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [closeOnboard, isOnboardOpen, loading])

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-3 sm:p-6 lg:flex lg:items-center lg:p-8">
      <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-blue-300/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-200/20 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-4xl">

        <div className="mb-3 flex items-end justify-between sm:mb-5 lg:block lg:text-center">
          <div style={{ fontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif" }}>
            <h2 className="text-lg font-bold tracking-tight text-slate-800 sm:text-2xl lg:text-3xl">Welcome to Loan Mitra</h2>
            <p className="mt-0.5 text-xs font-medium tracking-wide text-slate-500 sm:mt-1.5 sm:text-sm lg:text-base">Are you ready to onboard?</p>
          </div>
        </div>

        <div className="mx-auto grid max-w-lg gap-3 sm:gap-5">
          {sections.map(({ title, eyebrow, description, actionLabel, Icon, accent }) => {
            const styles = accentStyles[accent]

            return (
            <button
              type="button"
              key={title}
              onClick={() => setIsOnboardOpen(true)}
              className={`group relative overflow-hidden rounded-xl border border-white/80 bg-white/90 p-4 text-left shadow-[0_10px_35px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 sm:rounded-2xl sm:p-7 ${styles.card}`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${styles.line}`} />

              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1 sm:h-12 sm:w-12 sm:rounded-xl ${styles.icon}`}>
                  <Icon fontSize="medium" />
                </div>
              </div>

              <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:mt-6 sm:text-xs">{eyebrow}</p>
              <h3 className="mt-0.5 text-lg font-bold text-slate-800 sm:mt-1 sm:text-2xl">{title}</h3>
              <p className="mt-1.5 text-xs leading-5 text-slate-500 sm:mt-3 sm:min-h-[48px] sm:text-sm sm:leading-6">{description}</p>

              <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold sm:mt-6 sm:gap-2 sm:text-sm ${styles.action}`}>
                {actionLabel}
                <ArrowForwardIcon className="transition-transform duration-300 group-hover:translate-x-1" fontSize="small" />
              </div>
            </button>
            )
          })}
        </div>
        <p className="mt-5 text-center text-xs font-medium text-slate-500 sm:text-sm">Powered by Vanta Black Capital Consulting</p>
      </div>

      {isOnboardOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-[2px] sm:p-5"
          onMouseDown={event => event.target === event.currentTarget && closeOnboard()}
          role="presentation"
        >
          <div
            className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboard-title"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-6">
              <div>
                <h2 id="onboard-title" className="text-lg font-semibold text-slate-800">Onboard Merchant</h2>
                <p className="mt-0.5 text-xs text-slate-500">Enter the merchant details below.</p>
              </div>
              <button
                type="button"
                onClick={closeOnboard}
                disabled={loading}
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                aria-label="Close onboarding form"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <form onSubmit={submitOnboard} className="flex min-h-0 flex-1 flex-col">
              <div className="overflow-y-auto px-5 py-5 sm:px-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Name" required error={showError('name')}>
                    <input
                      autoFocus
                      value={form.name}
                      onChange={event => setValue('name', event.target.value)}
                      onBlur={() => setTouched(current => ({ ...current, name: true }))}
                      className={`${inputClass} ${showError('name') ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`}
                      placeholder="Merchant name"
                    />
                  </Field>

                  <Field label="Contact Number" required error={showError('contactNumber')}>
                    <input
                      value={form.contactNumber}
                      onChange={event => setValue('contactNumber', event.target.value.replace(/\D/g, '').slice(0, 10))}
                      onBlur={() => setTouched(current => ({ ...current, contactNumber: true }))}
                      className={`${inputClass} ${showError('contactNumber') ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`}
                      placeholder="10-digit contact number"
                      inputMode="numeric"
                    />
                  </Field>

                  <Field label="Email" required error={showError('email')}>
                    <input
                      type="email"
                      value={form.email}
                      onChange={event => setValue('email', event.target.value)}
                      onBlur={() => setTouched(current => ({ ...current, email: true }))}
                      className={`${inputClass} ${showError('email') ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`}
                      placeholder="merchant@example.com"
                    />
                  </Field>

                  <Field label="Location">
                    <input
                      value={form.location}
                      onChange={event => setValue('location', event.target.value)}
                      className={inputClass}
                      placeholder="Location"
                    />
                  </Field>

                  <Field label="Pincode" error={showError('pincode')}>
                    <input
                      value={form.pincode}
                      onChange={event => setValue('pincode', event.target.value.replace(/\D/g, '').slice(0, 6))}
                      onBlur={() => setTouched(current => ({ ...current, pincode: true }))}
                      className={`${inputClass} ${showError('pincode') ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`}
                      placeholder="6-digit pincode"
                      inputMode="numeric"
                    />
                  </Field>

                  <Field label="UPI ID" error={showError('upiId')}>
                    <input
                      value={form.upiId}
                      onChange={event => setValue('upiId', event.target.value)}
                      onBlur={() => setTouched(current => ({ ...current, upiId: true }))}
                      className={`${inputClass} ${showError('upiId') ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''}`}
                      placeholder="e.g. name@bank"
                    />
                  </Field>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
                <button
                  type="button"
                  onClick={closeOnboard}
                  disabled={loading}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="min-w-[130px] rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                >
                  {loading ? 'Saving…' : 'Save Merchant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  )
}
